import axios, { AxiosError } from 'axios';

import { logger } from '../utils/logger';

import { getAppConfig } from './config';
import { DEFAULT_API_TIMEOUT_MS } from './constants';

// Create component logger for ModelResolve
const modelResolveLogger = logger.withComponent('ModelResolve');

/**
 * Device information interface
 */
export interface DeviceInfo {
  session_id: string;
  data: {
    device_id: string;
    serial_number: string;
    model: string;
    organization_id: string;
  };
}

/**
 * Backend API response format
 * Success (200): { session_id: string, data: { device_id: string, serial_number: string, model: string } }
 * Error (4xx/5xx): { message: string }
 */
export type ModelResolveResponse = DeviceInfo | { message: string };

/**
 * Request payload interface for model resolve API
 */
export interface ModelResolveRequest {
  serial_number: string;
  captcha_token: string;
}

/**
 * Error types for model resolution based on backend error scenarios
 */
export enum ModelResolveErrorType {
  // eslint-disable-next-line no-unused-vars
  NETWORK_ERROR = 'NETWORK_ERROR',
  // eslint-disable-next-line no-unused-vars
  VALIDATION_ERROR = 'VALIDATION_ERROR', 
  // eslint-disable-next-line no-unused-vars
  DEVICE_NOT_FOUND = 'DEVICE_NOT_FOUND',
  // eslint-disable-next-line no-unused-vars
  RECAPTCHA_ERROR = 'RECAPTCHA_ERROR',
  // eslint-disable-next-line no-unused-vars
  SERVER_ERROR = 'SERVER_ERROR',
  // eslint-disable-next-line no-unused-vars
  RATE_LIMITED = 'RATE_LIMITED',
  // eslint-disable-next-line no-unused-vars
  METHOD_NOT_ALLOWED = 'METHOD_NOT_ALLOWED',
  // eslint-disable-next-line no-unused-vars
  UPSTREAM_ERROR = 'UPSTREAM_ERROR',
  // eslint-disable-next-line no-unused-vars
  UNKNOWN_ERROR = 'UNKNOWN_ERROR'
}

/**
 * Custom error class for model resolution with enhanced error information
 */
export class ModelResolveError extends Error {
  constructor(
    message: string,
    public type: ModelResolveErrorType,
    public code?: string,
    public statusCode?: number
  ) {
    super(message);
    this.name = 'ModelResolveError';
  }
}

/**
 * Resolve device model information by serial number
 * 
 * @param serial - Device serial number
 * @param recaptchaToken - reCAPTCHA verification token
 * @returns Promise resolving to device information
 * @throws ModelResolveError for various error scenarios
 */
export async function resolveDeviceModel(
  serial: string,
  recaptchaToken: string
): Promise<DeviceInfo> {
  const config = getAppConfig();
  
  // Client-side validation
  if (!serial?.trim()) {
    throw new ModelResolveError(
      __('Serial number is required'),
      ModelResolveErrorType.VALIDATION_ERROR,
      'MISSING_SERIAL'
    );
  }

  if (!recaptchaToken?.trim()) {
    throw new ModelResolveError(
      __('reCAPTCHA verification is required'),
      ModelResolveErrorType.RECAPTCHA_ERROR,
      'RECAPTCHA_REQUIRED'
    );
  }

  const requestData: ModelResolveRequest = {
    serial_number: serial.trim(),
    captcha_token: recaptchaToken
  };

  try {
    const response = await axios.post<ModelResolveResponse>(
      `${config.apiBaseUrl}/api/v1/cores/resolve-model`,
      requestData,
      {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: DEFAULT_API_TIMEOUT_MS,
      }
    );

    // If we reach here, it's a 2xx success status
    // Axios automatically throws for 4xx/5xx, so we can trust the response
    const deviceInfo = response.data as DeviceInfo;
    
    // Validate response structure (defensive programming)
    if (
      !deviceInfo.session_id ||
      !deviceInfo.data ||
      !deviceInfo.data.device_id ||
      !deviceInfo.data.model ||
      !deviceInfo.data.organization_id
    ) {
      throw new ModelResolveError(
        __("Invalid response format from server"),
        ModelResolveErrorType.SERVER_ERROR,
        "INVALID_RESPONSE_FORMAT"
      );
    }

    // Store session information in localStorage with TTL
    storeSessionData(deviceInfo);

    return deviceInfo;

  } catch (error) {
    // Re-throw ModelResolveError instances as-is
    if (error instanceof ModelResolveError) {
      throw error;
    }

    // Handle Axios errors (network issues, HTTP errors)
    if (axios.isAxiosError(error)) {
      throw handleAxiosError(error);
    }

    // Handle unexpected errors
    throw new ModelResolveError(
      __('An unexpected error occurred'),
      ModelResolveErrorType.UNKNOWN_ERROR,
      'UNKNOWN_ERROR'
    );
  }
}

/**
 * Handle Axios errors and convert to ModelResolveError with proper error mapping
 * Based on backend error codes and status codes from model-resolve.ts
 */
function handleAxiosError(error: AxiosError): never {
  // Handle timeout errors
  if (error.code === 'ECONNABORTED') {
    throw new ModelResolveError(
      __('Request timeout. Please try again.'),
      ModelResolveErrorType.NETWORK_ERROR,
      'TIMEOUT'
    );
  }

  // Handle network connectivity issues
  if (!error.response) {
    throw new ModelResolveError(
      __('Network error. Please check your connection.'),
      ModelResolveErrorType.NETWORK_ERROR,
      'NO_RESPONSE'
    );
  }

  const status = error.response.status;
  const responseData = error.response.data as { message?: string };
  const errorMessage = responseData?.message || 'An unexpected error occurred';

  // Map status codes to specific error types based on backend implementation
  switch (status) {
    case 400: // Bad Request - various validation errors
      return handle400Error(errorMessage, status);
      
    case 404: // Not Found - device not found
      throw new ModelResolveError(
        __('Device not found with the provided serial number'),
        ModelResolveErrorType.DEVICE_NOT_FOUND,
        'DEVICE_NOT_FOUND',
        status
      );

    case 405: // Method Not Allowed
      throw new ModelResolveError(
        __('Invalid request method. Please contact support.'),
        ModelResolveErrorType.METHOD_NOT_ALLOWED,
        'METHOD_NOT_ALLOWED',
        status
      );

    case 429: // Too Many Requests
      throw new ModelResolveError(
        __('Too many requests. Please try again later.'),
        ModelResolveErrorType.RATE_LIMITED,
        'RATE_LIMITED',
        status
      );

    case 500: // Internal Server Error
      throw new ModelResolveError(
        __('Internal server error. Please try again later.'),
        ModelResolveErrorType.SERVER_ERROR,
        'INTERNAL_ERROR',
        status
      );

    case 502: // Bad Gateway - upstream service errors
      throw new ModelResolveError(
        __('Service temporarily unavailable. Please try again later.'),
        ModelResolveErrorType.UPSTREAM_ERROR,
        'UPSTREAM_ERROR',
        status
      );

    case 504: // Gateway Timeout - upstream timeout
      throw new ModelResolveError(
        __('Request timeout while contacting upstream service. Please try again.'),
        ModelResolveErrorType.UPSTREAM_ERROR,
        'UPSTREAM_TIMEOUT',
        status
      );

    default:
      throw new ModelResolveError(
        errorMessage,
        ModelResolveErrorType.UNKNOWN_ERROR,
        'UNKNOWN_HTTP_ERROR',
        status
      );
  }
}

/**
 * Handle 400 Bad Request errors with specific error type mapping
 * Based on backend error codes from modelResolve.ts logic
 */
function handle400Error(errorMessage: string, status: number): never {
  // Map common validation error patterns to specific error types
  const lowerMessage = errorMessage.toLowerCase();
  
  if (lowerMessage.includes('serial number is required') || 
      lowerMessage.includes('missing_serial')) {
    throw new ModelResolveError(
      __('Serial number is required'),
      ModelResolveErrorType.VALIDATION_ERROR,
      'MISSING_SERIAL',
      status
    );
  }

  if (lowerMessage.includes('invalid serial number') || 
      lowerMessage.includes('invalid_serial_format')) {
    throw new ModelResolveError(
      __('Invalid serial number. Must contain only letters and numbers, and be less than 30 characters.'),
      ModelResolveErrorType.VALIDATION_ERROR,
      'INVALID_SERIAL_FORMAT',
      status
    );
  }

  if (lowerMessage.includes('recaptcha token is required') || 
      lowerMessage.includes('recaptcha_required')) {
    throw new ModelResolveError(
      __('reCAPTCHA verification is required'),
      ModelResolveErrorType.RECAPTCHA_ERROR,
      'RECAPTCHA_REQUIRED',
      status
    );
  }

  if (lowerMessage.includes('invalid recaptcha') || 
      lowerMessage.includes('recaptcha_invalid')) {
    throw new ModelResolveError(
      __('reCAPTCHA verification failed. Please try again.'),
      ModelResolveErrorType.RECAPTCHA_ERROR,
      'RECAPTCHA_INVALID',
      status
    );
  }

  if (lowerMessage.includes('request body is required') || 
      lowerMessage.includes('missing_body')) {
    throw new ModelResolveError(
      __('Request body is required'),
      ModelResolveErrorType.VALIDATION_ERROR,
      'MISSING_BODY',
      status
    );
  }

  if (lowerMessage.includes('invalid json') || 
      lowerMessage.includes('invalid_json')) {
    throw new ModelResolveError(
      __('Invalid request format'),
      ModelResolveErrorType.VALIDATION_ERROR,
      'INVALID_JSON',
      status
    );
  }

  // Default to validation error for other 400 responses
  throw new ModelResolveError(
    errorMessage,
    ModelResolveErrorType.VALIDATION_ERROR,
    'VALIDATION_ERROR',
    status
  );
}

/**
 * Get user-friendly error message based on error type
 * 
 * @param error - ModelResolveError instance
 * @returns User-friendly error message
 */
export function getErrorMessage(error: ModelResolveError): string {
  switch (error.type) {
    case ModelResolveErrorType.VALIDATION_ERROR:
      return error.message;
      
    case ModelResolveErrorType.DEVICE_NOT_FOUND:
      return __('Device not found. Please check the serial number and try again.');
      
    case ModelResolveErrorType.RECAPTCHA_ERROR:
      return __('Security verification failed. Please complete the reCAPTCHA and try again.');
      
    case ModelResolveErrorType.NETWORK_ERROR:
      return __('Connection error. Please check your internet connection and try again.');
      
    case ModelResolveErrorType.RATE_LIMITED:
      return __('Too many requests. Please wait a moment and try again.');
      
    case ModelResolveErrorType.SERVER_ERROR:
    case ModelResolveErrorType.UPSTREAM_ERROR:
      return __('Service temporarily unavailable. Please try again in a few moments.');
      
    case ModelResolveErrorType.METHOD_NOT_ALLOWED:
      return __('Request error. Please contact support if this persists.');
      
    default:
      return __('An unexpected error occurred. Please try again.');
  }
}

/**
 * Check if error is retryable (user can try the same action again)
 * 
 * @param error - ModelResolveError instance
 * @returns true if the error is retryable
 */
export function isRetryableError(error: ModelResolveError): boolean {
  const retryableTypes = [
    ModelResolveErrorType.NETWORK_ERROR,
    ModelResolveErrorType.SERVER_ERROR,
    ModelResolveErrorType.UPSTREAM_ERROR
  ];
  
  return retryableTypes.includes(error.type) && error.code !== 'RATE_LIMITED';
}

/**
 * Stores session data in localStorage with TTL
 * Valid for SESSION_TTL_MINUTES minutes, removes expired items
 * 
 * @param sessionInfo - Session information to store
 */
function storeSessionData(sessionInfo: DeviceInfo): void {
  // Get SESSION_TTL_MINUTES from environment (default 60 minutes)
  const ttlMinutes = parseInt(process.env.REACT_APP_SESSION_TTL_MINUTES || '60', 10);
  const expirationTime = Date.now() + (ttlMinutes * 60 * 1000);

  try {
    // Store session ID
    localStorage.setItem('core_setup_session_id', JSON.stringify({
      value: sessionInfo.session_id,
      expiresAt: expirationTime
    }));

    // Store device serial number
    localStorage.setItem('core_setup_device_serial_number', JSON.stringify({
      value: sessionInfo.data.serial_number,
      expiresAt: expirationTime
    }));

    // Store device model
    localStorage.setItem('core_setup_device_model', JSON.stringify({
      value: sessionInfo.data.model,
      expiresAt: expirationTime
    }));

    // Store device org id
    localStorage.setItem('core_setup_device_org_id', JSON.stringify({
      value: sessionInfo.data.organization_id,
      expiresAt: expirationTime
    }));

    // Delete device sku, if exists
    localStorage.removeItem('core_setup_device_sku');

    // eslint-disable-next-line no-console
    modelResolveLogger.info('Session data stored in localStorage', {
      sessionId: sessionInfo.session_id,
      serialNumber: sessionInfo.data.serial_number,
      model: sessionInfo.data.model,
      expiresAt: new Date(expirationTime).toISOString()
    });

  } catch (error) {
    // eslint-disable-next-line no-console
    modelResolveLogger.warn('Failed to store session data in localStorage:', error instanceof Error ? error : new Error(String(error)));
  }
}

/**
 * Cleans up expired localStorage items
 */
function cleanupExpiredLocalStorage(): void {
  const now = Date.now();
  const keysToCheck = [
    'core_setup_session_id',
    'core_setup_device_serial_number', 
    'core_setup_device_model'
  ];

  keysToCheck.forEach(key => {
    try {
      const item = localStorage.getItem(key);
      if (item) {
        const parsedItem = JSON.parse(item);
        if (parsedItem.expiresAt && parsedItem.expiresAt < now) {
          localStorage.removeItem(key);
          // eslint-disable-next-line no-console
          modelResolveLogger.info(`Removed expired localStorage item: ${key}`);
        }
      }
    } catch (error) {
      // Invalid JSON, remove the item
      localStorage.removeItem(key);
      // eslint-disable-next-line no-console
      modelResolveLogger.warn(`Removed invalid localStorage item: ${key}`, error instanceof Error ? error : new Error(String(error)));
    }
  });
}

/**
 * Retrieves session data from localStorage if still valid
 * 
 * @returns Session data if valid, null if expired or not found
 */
export function getStoredSessionData(): { sessionId: string; serialNumber: string; model: string } | null {
  try {
    const sessionIdItem = localStorage.getItem('core_setup_session_id');
    const serialNumberItem = localStorage.getItem('core_setup_device_serial_number');
    const modelItem = localStorage.getItem('core_setup_device_model');

    if (!sessionIdItem || !serialNumberItem || !modelItem) {
      return null;
    }

    const sessionId = JSON.parse(sessionIdItem);
    const serialNumber = JSON.parse(serialNumberItem);
    const model = JSON.parse(modelItem);

    const now = Date.now();

    // Check if any item is expired
    if (sessionId.expiresAt < now || serialNumber.expiresAt < now || model.expiresAt < now) {
      // Clean up expired items
      cleanupExpiredLocalStorage();
      return null;
    }

    return {
      sessionId: sessionId.value,
      serialNumber: serialNumber.value,
      model: model.value
    };

  } catch (error) {
    // eslint-disable-next-line no-console
    modelResolveLogger.warn('Failed to retrieve session data from localStorage:', error instanceof Error ? error : new Error(String(error)));
    cleanupExpiredLocalStorage();
    return null;
  }
}
